import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import { useSeo } from "@/lib/useSeo";

/**
 * ArticleLayout — reusable template for SEO content articles
 * under "/rady-pro-spravu-uklidu" content hub.
 *
 * Usage:
 *   <ArticleLayout
 *     title="Kolik stojí úklid kanceláří v Brně?"
 *     metaTitle="Kolik stojí úklid kanceláří v Brně? | SANVIORO"
 *     description="Cenotvorba úklidu kanceláří v Brně — co ovlivňuje cenu..."
 *     path="/kolik-stoji-uklid-kancelari-brno"
 *     publishedAt="2026-06-01"
 *     readingTime="5 min"
 *   >
 *     <p>First paragraph…</p>
 *     <h2>Section heading</h2>
 *     …
 *   </ArticleLayout>
 *
 * The wrapper handles:
 *  - <Header /> / <Footer />
 *  - Dynamic SEO via useSeo()
 *  - Schema.org Article JSON-LD
 *  - Standard typography (prose-like classes)
 *  - Breadcrumb back to /rady-pro-spravu-uklidu
 *  - Internal-link block to main service pages (boost topical authority)
 */
const ArticleLayout = ({
  title,
  metaTitle,
  description,
  path,
  publishedAt,
  readingTime,
  children,
}) => {
  useSeo({ title: metaTitle || title, description, path });

  const ldJson = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    datePublished: publishedAt,
    inLanguage: "cs-CZ",
    author: {
      "@type": "Organization",
      name: "SANVIORO",
      url: "https://sanvioro.cz/",
    },
    publisher: {
      "@type": "Organization",
      name: "Sanvioro s.r.o.",
      url: "https://sanvioro.cz/",
    },
    mainEntityOfPage: `https://sanvioro.cz${path}`,
  };

  return (
    <div
      className="App font-body text-[#111111] bg-white"
      data-testid="article-layout"
    >
      <Header />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }}
      />

      <main className="bg-white pt-32 md:pt-36 pb-16 md:pb-20">
        <div className="mx-auto max-w-[820px] px-6 md:px-10">
          <Link
            to="/rady-pro-spravu-uklidu"
            className="inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.18em] text-[#0F5C69] hover:gap-3 transition-all"
          >
            <ArrowLeft size={14} strokeWidth={2.5} />
            Rady pro správu úklidu
          </Link>

          <h1
            data-testid="article-h1"
            className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-[-0.025em] leading-[1.05] text-[#111111]"
          >
            {title}
          </h1>

          {(publishedAt || readingTime) && (
            <div className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-[#666666]">
              {publishedAt && (
                <time dateTime={publishedAt}>{publishedAt}</time>
              )}
              {publishedAt && readingTime && (
                <span className="text-[#CCCCCC]">·</span>
              )}
              {readingTime && <span>{readingTime} čtení</span>}
            </div>
          )}

          <article
            className="mt-10 text-[#222222] leading-[1.75] text-[17px]
              [&>p]:mb-5
              [&>h2]:font-display [&>h2]:text-2xl [&>h2]:md:text-3xl [&>h2]:font-bold [&>h2]:tracking-tight [&>h2]:text-[#111111] [&>h2]:mt-12 [&>h2]:mb-5
              [&>h3]:font-display [&>h3]:text-xl [&>h3]:md:text-2xl [&>h3]:font-semibold [&>h3]:tracking-tight [&>h3]:text-[#111111] [&>h3]:mt-10 [&>h3]:mb-4
              [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-5 [&>ul]:space-y-2 [&>ul]:marker:text-[#0F5C69]
              [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-5 [&>ol]:space-y-2 [&>ol]:marker:text-[#0F5C69]
              [&_a]:text-[#0F5C69] [&_a]:underline [&_a]:decoration-[#0F5C69]/40 [&_a]:underline-offset-4 [&_a]:hover:decoration-[#0F5C69]"
          >
            {children}
          </article>
        </div>
      </main>

      {/* Internal linking — strengthens topical authority across the cluster */}
      <section className="bg-[#F5F5F7] py-14 md:py-16 border-t border-[#E5E5E5]">
        <div className="mx-auto max-w-[1100px] px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.28em] font-bold text-[#0F5C69]">
            Naše služby v Brně
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-4 text-[#111111]">
            Pokračujte na konkrétní službu.
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { to: "/uklid-kancelari-brno", title: "Úklid kanceláří v Brně" },
              { to: "/uklid-provozoven-brno", title: "Úklid provozoven v Brně" },
              { to: "/uklid-bytovych-domu-brno", title: "Úklid bytových domů v Brně" },
            ].map((s) => (
              <Link
                key={s.to}
                to={s.to}
                className="group flex items-center justify-between gap-3 bg-white border border-[#E5E5E5] rounded-xl px-5 py-4 hover:border-[#0F5C69] transition-colors"
              >
                <span className="font-display text-base md:text-lg font-semibold tracking-tight text-[#111111]">
                  {s.title}
                </span>
                <ArrowRight
                  size={16}
                  strokeWidth={2.5}
                  className="shrink-0 text-[#0F5C69] transition-transform group-hover:translate-x-1"
                />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ArticleLayout;
